import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "@twilio-paste/core/box";
import { Text } from "@twilio-paste/core/text";
import { UserIcon } from "@twilio-paste/icons/esm/UserIcon";

import { AppState } from "../store/definitions";
import { updateAwaitingBot } from "../store/actions/genericActions";
import { isHumanMode } from "../store/selectors";
import {
    outerContainerStyles,
    bubbleAndAvatarContainerStyles,
    getAvatarContainerStyles,
    getInnerContainerStyles,
    authorStyles
} from "./styles/MessageBubble.styles";

/*
 * "Bot is typing" bubble, part of the unified "awaiting bot" state (chat.awaitingBot):
 * shown while we wait for the bot. The wait is entered instantly on the client (message sent
 * or button clicked) or by the backend signal (conversation.attributes.botTyping === true).
 * Here we own the two automatic exits: a new bot message arriving, and a safety timeout.
 */
const SAFETY_TIMEOUT_MS = 45000;

interface SenderAttributes {
    sender?: { name?: string; avatar?: string };
}

export const TypingIndicator = () => {
    const dispatch = useDispatch();
    const { awaitingBot, messages, conversationsClient, humanMode } = useSelector((state: AppState) => ({
        awaitingBot: state.chat.awaitingBot,
        messages: state.chat.messages,
        conversationsClient: state.chat.conversationsClient,
        humanMode: isHumanMode(state.chat)
    }));

    const currentUserId = conversationsClient?.user.identity;
    const lastMessage = messages?.[messages.length - 1];
    const prevLastSid = useRef(lastMessage?.sid);

    // Release as soon as a new bot message lands, so the bubble never overlaps the message.
    useEffect(() => {
        if (lastMessage?.sid !== prevLastSid.current) {
            prevLastSid.current = lastMessage?.sid;
            if (awaitingBot && lastMessage && lastMessage.author !== currentUserId) {
                dispatch(updateAwaitingBot(false));
            }
        }
    }, [lastMessage, currentUserId, awaitingBot, dispatch]);

    // Safety net: release on its own if the bot never answers / the backend never clears it.
    useEffect(() => {
        if (!awaitingBot) {
            return undefined;
        }
        const timer = setTimeout(() => dispatch(updateAwaitingBot(false)), SAFETY_TIMEOUT_MS);
        return () => clearTimeout(timer);
    }, [awaitingBot, dispatch]);

    // Em modo humano (pós-transbordo) o balão nunca aparece — quem responde é o atendente.
    if (!awaitingBot || humanMode) {
        return null;
    }

    const lastBotMessage = [...(messages || [])].reverse().find((m) => m.author !== currentUserId);
    const { sender } = (lastBotMessage?.attributes ?? {}) as SenderAttributes;
    const author = sender?.name || "Assistente";

    return (
        <Box
            {...outerContainerStyles}
            role="status"
            aria-live="polite"
            aria-label={`${author} está digitando`}
            data-test="bot-typing-indicator"
        >
            <Box {...bubbleAndAvatarContainerStyles}>
                <Box {...getAvatarContainerStyles(false)} data-test="bot-typing-avatar">
                    {sender?.avatar ? (
                        <img
                            src={sender.avatar}
                            alt={author}
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                    ) : (
                        <UserIcon decorative={true} size="sizeIcon40" />
                    )}
                </Box>
                <Box {...getInnerContainerStyles(false)}>
                    <Text {...authorStyles} as="p" marginBottom="space20" title={author}>
                        {author}
                    </Text>
                    <span className="syncia-typing-dots" aria-hidden="true">
                        <span />
                        <span />
                        <span />
                    </span>
                </Box>
            </Box>
        </Box>
    );
};
